import * as React from "react";
import type { Ordering } from "@/types/base";
import { Button } from "@/components/ui/button";
import { SnapshotGridTable } from "@/modules/snapshots/grid/table";
import { SnapshotGridViewSearch } from "@/modules/snapshots/grid/search";
import { isColumnFilterActive, type ColumnFilters } from "@/modules/snapshots/grid/sortAndFilter";
import type { SnapshotGridQueryPayload } from "@/modules/snapshots/grid/gridQueryPayload";
import { mergeSnapshotGridRows } from "@/modules/snapshots/grid/gridInfiniteRows";
import { useSnapshotGridDataSource, type SnapshotGridDataSourcePage } from "@/modules/snapshots/grid/dataSource";
import type { SnapshotGridRowModel } from "@/modules/snapshots/grid/rowModel";
import type { PipelineSummaryGridRowKind } from "@/modules/snapshots/grid/pipelinePreview.types";
import { PipelinePreviewDrawer } from "@/modules/snapshots/grid/pipelinePreviewDrawer";
import type { StagedValue } from "@/types/contract-family-pipeline";
import { patchPipelineSummaryGridRows } from "@/modules/snapshots/grid/pipelinePreviewCache";
import { PIPELINE_DEFAULT_ORDERING, PIPELINE_PAGE_SIZE } from "@/modules/snapshots/grid/constants";
import type { PipelinePreviewProps } from "@/modules/snapshots/grid/types";

type PipelineIssue = SnapshotGridDataSourcePage["issues"][number];

const pipelineIssueKey = (issue: PipelineIssue) => `${issue.familyId}:${issue.fieldId}`;

const mergePipelineIssues = (previousIssues: PipelineIssue[], nextIssues: PipelineIssue[]): PipelineIssue[] => {
  const issuesByKey = new Map(previousIssues.map(issue => [pipelineIssueKey(issue), issue]));
  for (const issue of nextIssues) issuesByKey.set(pipelineIssueKey(issue), issue);
  return Array.from(issuesByKey.values());
};

export const PipelineSnapshotGridCore: React.FC<PipelinePreviewProps> = ({
  pipelineRunId,
  capabilities,
  pipelineReadOnly = false,
  pipelineOwnerNamesById,
  initialQuery,
  onQueryChange,
}) => {
  const initialColumnFilters = initialQuery?.column_filters as ColumnFilters | undefined;
  const [query, setQuery] = React.useState(initialQuery?.query ?? "");
  const [ordering, setOrdering] = React.useState<Ordering>(initialQuery?.ordering ?? PIPELINE_DEFAULT_ORDERING);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFilters>(initialColumnFilters ?? {});
  const [pageOffset, setPageOffset] = React.useState(0);
  const [selectedCellId, setSelectedCellId] = React.useState<string | null>(null);
  const [openedCell, setOpenedCell] = React.useState<{
    rowKind: PipelineSummaryGridRowKind;
    rowId: string;
    stagedOutcomeId: string | null;
    outcomeId: string | null;
    fieldId: string;
    readOnly: boolean;
  } | null>(null);
  const [loadedGridRows, setLoadedGridRows] = React.useState<SnapshotGridRowModel[]>([]);
  const [loadedPage, setLoadedPage] = React.useState<SnapshotGridDataSourcePage | null>(null);
  const [gridMetadata, setGridMetadata] = React.useState<SnapshotGridDataSourcePage | null>(null);
  const [loadedIssues, setLoadedIssues] = React.useState<SnapshotGridDataSourcePage["issues"]>([]);
  const resetLoadedPipelineResults = React.useCallback((clearMetadata = false) => {
    setPageOffset(0);
    setSelectedCellId(null);
    setOpenedCell(null);
    setLoadedGridRows([]);
    setLoadedPage(null);
    setLoadedIssues([]);
    if (clearMetadata) setGridMetadata(null);
  }, []);
  const handleQueryChange = React.useCallback(
    (nextQuery: string) => {
      resetLoadedPipelineResults();
      setQuery(nextQuery);
    },
    [resetLoadedPipelineResults],
  );
  const handleOrderingChange = React.useCallback(
    (nextOrdering: Ordering) => {
      resetLoadedPipelineResults();
      setOrdering(nextOrdering);
    },
    [resetLoadedPipelineResults],
  );
  const gridQueryPayload = React.useMemo<SnapshotGridQueryPayload>(
    () => ({
      offset: pageOffset,
      limit: PIPELINE_PAGE_SIZE,
      query,
      ordering,
      column_filters: columnFilters,
    }),
    [columnFilters, ordering, pageOffset, query],
  );
  const { page, query: activeGridQuery } = useSnapshotGridDataSource({
    mode: "pipeline-preview",
    pipelineRunId,
    payload: gridQueryPayload,
    capabilities,
  });

  React.useEffect(() => {
    setQuery(initialQuery?.query ?? "");
    setOrdering(initialQuery?.ordering ?? PIPELINE_DEFAULT_ORDERING);
    setColumnFilters(initialColumnFilters ?? {});
    resetLoadedPipelineResults(true);
  }, [initialColumnFilters, initialQuery?.ordering, initialQuery?.query, pipelineRunId, resetLoadedPipelineResults]);

  React.useEffect(() => {
    onQueryChange?.(gridQueryPayload);
  }, [gridQueryPayload, onQueryChange]);

  React.useEffect(() => {
    if (!page) return;
    setLoadedPage(page);
    setGridMetadata(page);
    setLoadedGridRows(previousRows =>
      page.pagination.offset === 0 ? page.rows : mergeSnapshotGridRows(previousRows, page.rows),
    );
    setLoadedIssues(previousIssues =>
      page.pagination.offset === 0 ? page.issues : mergePipelineIssues(previousIssues, page.issues),
    );
  }, [page]);

  const displayPage = loadedPage ?? gridMetadata;
  const pagination = loadedPage?.pagination;
  const loadedFamilyCount = pagination ? (pagination.offset + 1) * pagination.limit : 0;
  const hasNextGridPage = Boolean(pagination && loadedFamilyCount < pagination.total);
  const issues = loadedIssues;
  const issueTotal = loadedPage?.issueTotal ?? 0;
  const isInitialLoading = activeGridQuery.isLoading && gridMetadata === null;
  const hasActiveFilters = Object.values(columnFilters).some(isColumnFilterActive);
  const hasActiveRefinement = query.length > 0 || hasActiveFilters;
  const emptyMessage = hasActiveRefinement
    ? "No family summaries match your search or filters."
    : "No family summaries are available.";
  const isRefinementLoading = activeGridQuery.isFetching && loadedPage === null && gridMetadata !== null;
  const hasPostInitialError = activeGridQuery.isError && gridMetadata !== null;
  const resultTotal = pagination?.total ?? 0;
  const statusMessage = isRefinementLoading
    ? "Updating family summaries…"
    : hasPostInitialError
      ? "Unable to update family summaries. Please try again."
      : loadedGridRows.length === 0
        ? emptyMessage
        : `${resultTotal} matching ${resultTotal === 1 ? "family" : "families"}.`;

  if (activeGridQuery.isError && gridMetadata === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <p className="text-red-600">Unable to load the family summary grid.</p>
        <button
          className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          onClick={() => void activeGridQuery.refetch()}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (isInitialLoading || !displayPage) {
    return <p className="p-6 text-sm text-gray-500">Loading summary review…</p>;
  }

  return (
    <div className="flex min-h-0 flex-col gap-3 overflow-auto p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="w-full max-w-xl">
          <SnapshotGridViewSearch
            query={query}
            setQuery={handleQueryChange}
            isFetching={activeGridQuery.isFetching}
            placeholder="Search family summaries"
            ariaLabel="Search family summaries"
            searchRole
          />
        </div>
        {hasActiveRefinement ? (
          <Button
            type="button"
            size="sm"
            variant="second-white"
            onClick={() => {
              resetLoadedPipelineResults();
              setQuery("");
              setColumnFilters({});
            }}
          >
            Clear all
          </Button>
        ) : null}
      </div>
      <div
        role={hasPostInitialError ? "alert" : "status"}
        aria-live={hasPostInitialError ? "assertive" : "polite"}
        className={hasPostInitialError ? "flex items-center gap-2 text-sm text-red-700" : "text-xs text-slate-600"}
      >
        <span>{statusMessage}</span>
        {hasPostInitialError ? (
          <Button type="button" size="sm" variant="second-white" onClick={() => void activeGridQuery.refetch()}>
            Retry
          </Button>
        ) : null}
      </div>
      {issueTotal > 0 && (
        <div role="alert" className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
          <p>{issueTotal} summary fields could not be generated</p>
          <ul className="mt-1 list-disc pl-5">
            {issues.slice(0, 10).map(issue => (
              <li key={pipelineIssueKey(issue)}>
                {issue.familyName} — {issue.fieldName}
              </li>
            ))}
          </ul>
        </div>
      )}
      <SnapshotGridTable
        rows={loadedGridRows}
        fields={displayPage.fields}
        sourceCollectionId={displayPage.sourceCollectionId}
        templateId={displayPage.templateId}
        currentOrdering={ordering}
        setOrdering={handleOrderingChange}
        selectedGridRowId={selectedCellId}
        onSelectedGridRowChange={setSelectedCellId}
        onOpenCell={(row, cell) => {
          const fieldId = cell?.fieldId;
          const rowKind = row.presentation.kind;
          if (!fieldId || (rowKind !== "family_summary" && rowKind !== "source_document")) {
            return;
          }
          setSelectedCellId(row.id);
          setOpenedCell({
            rowKind,
            rowId: row.id,
            stagedOutcomeId: row.presentation.stagedOutcomeId,
            outcomeId: row.presentation.outcomeId,
            fieldId,
            readOnly: pipelineReadOnly || !row.presentation.editable || row.presentation.stagedOutcomeId === null,
          });
        }}
        columnFilters={columnFilters}
        onColumnFilterChange={(columnName, value) => {
          if (!displayPage.fields.some(field => !field.is_note && field.key === columnName)) return;
          resetLoadedPipelineResults();
          setColumnFilters(currentFilters => {
            const nextFilters = { ...currentFilters };
            if (value === undefined) delete nextFilters[columnName];
            else nextFilters[columnName] = value;
            return nextFilters;
          });
        }}
        emptyMessage={
          hasPostInitialError
            ? "Family summaries are temporarily unavailable."
            : isRefinementLoading
              ? "Updating family summaries…"
              : emptyMessage
        }
        ownerNamesById={pipelineOwnerNamesById}
      />
      {openedCell ? (
        <PipelinePreviewDrawer
          open
          onOpenChange={open => {
            if (!open) setOpenedCell(null);
          }}
          runId={pipelineRunId}
          rowKind={openedCell.rowKind}
          rowId={openedCell.rowId}
          stagedOutcomeId={openedCell.stagedOutcomeId}
          fieldId={openedCell.fieldId}
          readOnly={openedCell.readOnly}
          onSaved={(value: StagedValue) => {
            const stagedOutcomeId = openedCell.stagedOutcomeId;
            if (!stagedOutcomeId || !loadedPage) return;
            setLoadedGridRows(currentRows =>
              patchPipelineSummaryGridRows(currentRows, loadedPage.fields, stagedOutcomeId, value.field_id, value),
            );
          }}
        />
      ) : null}
      {hasNextGridPage && !hasPostInitialError && (
        <button
          type="button"
          className="self-start rounded border px-3 py-2 text-sm"
          disabled={activeGridQuery.isFetching}
          onClick={() => setPageOffset(currentOffset => currentOffset + 1)}
        >
          {activeGridQuery.isFetching ? "Loading more families" : "Load more families"}
        </button>
      )}
    </div>
  );
};
